import React, { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { useTheme } from "@mui/system";
import dayjs from "dayjs";
import DatagridComponent from "../../../../components/datagridComponent";
import {
  Box,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  Divider,
  Chip,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NoHostScreen from "../noHostScreen";
import useStyles from "./styles";

const timeZone = "America/Sao_Paulo";

// Texto pré-quebrado: é desenhado dentro do SVG da ilustração, que não quebra
// linha sozinho (máx. ~28 caracteres por linha)
const SEM_DADOS_LICENCAS = `Este equipamento não possui\ncoleta de licenças\nconfigurada.`;

export default function LicencasPanel({
  serieTemporalUrl = "/zabbix/serieTemporal",
} = {}) {
  const { api, filterData, selectTime, optionsTime } = useOutletContext();
  const theme = useTheme();
  const styles = useStyles(theme);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // Só decide "sem licenças" depois de uma busca concluída, para não piscar a
  // tela vazia no primeiro paint nem quando o filtro está incompleto
  const [buscou, setBuscou] = useState(false);

  const controllerRef = useRef(null);

  const getStatusChip = (unixTimestamp) => {
    if (!unixTimestamp || isNaN(unixTimestamp) || unixTimestamp === 0) {
      return (
        <Tooltip title="Sem informações">
          <Chip sx={styles.chipDefault} label="Sem informações" />
        </Tooltip>
      );
    }

    // Convertendo unix (segundos) para timestamp
    const expirationDate = dayjs.unix(unixTimestamp);
    const diffDays = expirationDate.diff(dayjs(), "day");

    if (diffDays > 30) {
      return (
        <Tooltip title="Em vigência">
          <Chip sx={styles.chipVigencia} label="Em vigência" />
        </Tooltip>
      );
    }
    if (diffDays > 20 && diffDays <= 30) {
      return (
        <Tooltip title="Próximo de expirar">
          <Chip sx={styles.chipProxima} label="Próxima de expirar" />
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Expirado">
        <Chip sx={styles.chipExpirado} label="Expirado" />
      </Tooltip>
    );
  };

  useEffect(() => {
    // Verifica campos obrigatórios da mesma forma que no vpnPanel
    const allFieldsFilled =
      filterData?.hostId &&
      filterData?.fabricante &&
      filterData?.funcao &&
      filterData?.modelo;

    if (!allFieldsFilled) {
      if (controllerRef.current) controllerRef.current.abort();
      setData([]);
      setLoading(false);
      setBuscou(false);
      return;
    }

    let dateInitial = filterData?.initialDate;
    let finalDate = filterData?.finalDate;
    if (selectTime || selectTime === 0) {
      dateInitial = optionsTime?.[selectTime]?.value;
      finalDate = dayjs()?.set("second", 0)?.set("millisecond", 0)?.toDate();
    }

    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setData([]);
    setLoading(true);
    setBuscou(false);

    const fetchLicencas = async () => {
      try {
        const response = await api.post(
          serieTemporalUrl,
          {
            hostids: filterData?.hostId,
            device: filterData?.device,
            fabricante: filterData?.fabricante,
            funcao: filterData?.funcao,
            modelo: filterData?.modelo,
            dash: "firewall",
            nameFilter: "licencas",
            lastItem: true,
            lastDay: true,
            forceObject: true,
            initial_date: dateInitial,
            final_date: finalDate,
          },
          { signal: controller.signal },
        );

        if (!controller.signal.aborted) {
          const resData = response?.data || {};
          const parsedData = [];

          // O backend (se forceObject=true) retorna as chaves do zabbix como properties.
          // Iteramos pelo objeto e montamos a lista.
          Object.entries(resData).forEach(([key, entry]) => {
            const rawName = entry?.name || key;
            const cleanName = rawName.replace(/^Licença\s+/i, ""); // Remove "Licença " do início
            const value = entry?.value;

            parsedData.push({
              id: key, // DataGrid requer uma propriedade 'id'
              name: cleanName,
              unixValue: value,
            });
          });

          setData(parsedData);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Erro ao buscar licenças:", error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setBuscou(true);
        }
      }
    };

    fetchLicencas();
  }, [
    filterData?.hostId,
    filterData?.fabricante,
    filterData?.funcao,
    filterData?.modelo,
    filterData?.device,
    filterData?.initialDate,
    filterData?.finalDate,
    selectTime,
  ]);

  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  const filteredData = data.filter((row) =>
    row.name.toLowerCase().includes(search.toLowerCase()),
  );

  const columns = [
    {
      field: "name",
      headerName: "Nome da Licença",
      flex: 1,
      minWidth: 200,
      renderHeader: () => <b>Nome da Licença</b>,
    },
    {
      field: "statusLicenca",
      headerName: "Status Licença",
      width: 180,
      renderHeader: () => <b>Status Licença</b>,
      renderCell: (params) => {
        const val = params.row.unixValue;
        return getStatusChip(val);
      },
    },
    {
      field: "unixValue",
      headerName: "Data de Expiração",
      width: 220,
      renderHeader: () => <b>Data de Expiração</b>,
      renderCell: (params) => {
        const val = params.value;
        const isValidDate = val && !isNaN(val) && val !== 0;
        const dateText = isValidDate
          ? dayjs.unix(val).format("DD/MM/YY")
          : "N/A";

        return <Typography variant="body2">{dateText}</Typography>;
      },
      align: "right",
      headerAlign: "right",
    },
  ];

  // Nenhuma licença coletada: métrica ausente no schema ou item inexistente
  if (buscou && !loading && data?.length === 0) {
    return <NoHostScreen mensagem={SEM_DADOS_LICENCAS} />;
  }

  return (
    <Box sx={styles.licencasContainer}>
      <Paper elevation={1} sx={styles.licencasPaper}>
        <Box sx={styles.licencasHeader}>
          <Typography variant="h6" sx={styles.licencasTitle}>
            Licenças
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Pesquisar licença..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={styles.licencasSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Divider />

        <Box sx={styles.licencasDatagridContainer}>
          <DatagridComponent
            rows={filteredData}
            columns={columns}
            loading={loading}
            isPagination={true}
            sxDatagrid={{
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
            }}
            options={{
              initialState: {
                sorting: {
                  sortModel: [{ field: "name", sort: "asc" }],
                },
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
